// API wrappers for the Galleries feature. Photo uploads go through the Media
// library (POST /tenant/media — type/size/quota validation, display +
// thumbnail variants), then the metadata row is registered against the
// gallery. Existing library items can be added to a gallery without a new
// upload.
import type { ApiClient } from "./api";
import { mediaUrl, uploadMedia, type MediaItem } from "./media";

export interface GalleryImage {
  id: string;
  key: string;
  thumbKey: string | null;
  width: number | null;
  height: number | null;
  caption: string;
  alt: string;
  /** Transient preview URL from the upload response; not persisted. */
  url?: string | null;
  thumbUrl?: string | null;
}

export interface Gallery {
  id: string;
  name: string;
  updated_at?: string;
  images: GalleryImage[];
}

export interface GalleriesResponse {
  galleries: Gallery[];
  /** Public R2 base URL for displaying stored images; null when unresolved. */
  assetsBaseUrl: string | null;
}

export async function fetchGalleries(api: ApiClient): Promise<GalleriesResponse> {
  const data = await api.request<{ galleries?: Gallery[]; assets_base_url?: string | null }>(
    "/tenant/galleries"
  );
  return { galleries: data.galleries ?? [], assetsBaseUrl: data.assets_base_url ?? null };
}

/** Resolve an image's display URL: transient upload URL first, then the key. */
export const imageUrl: (
  image: GalleryImage,
  assetsBaseUrl: string | null,
  options?: { thumb?: boolean }
) => string | null = mediaUrl;

export function createGallery(api: ApiClient, name: string): Promise<Gallery> {
  return api.request("/tenant/galleries", {
    method: "POST",
    body: JSON.stringify({ name })
  });
}

export function renameGallery(
  api: ApiClient,
  galleryId: string,
  name: string
): Promise<{ id: string; name: string }> {
  return api.request(`/tenant/galleries/${galleryId}`, {
    method: "PUT",
    body: JSON.stringify({ name })
  });
}

export function reorderGalleries(api: ApiClient, ids: string[]): Promise<unknown> {
  return api.request("/tenant/galleries/order", {
    method: "PUT",
    body: JSON.stringify({ ids })
  });
}

export function deleteGallery(api: ApiClient, galleryId: string): Promise<unknown> {
  return api.request(`/tenant/galleries/${galleryId}`, { method: "DELETE" });
}

export function updateGalleryImage(
  api: ApiClient,
  galleryId: string,
  imageId: string,
  fields: { caption: string; alt: string }
): Promise<unknown> {
  return api.request(`/tenant/galleries/${galleryId}/images/${imageId}`, {
    method: "PUT",
    body: JSON.stringify(fields)
  });
}

export function reorderGalleryImages(
  api: ApiClient,
  galleryId: string,
  ids: string[]
): Promise<unknown> {
  return api.request(`/tenant/galleries/${galleryId}/images/order`, {
    method: "PUT",
    body: JSON.stringify({ ids })
  });
}

export function deleteGalleryImage(
  api: ApiClient,
  galleryId: string,
  imageId: string
): Promise<unknown> {
  return api.request(`/tenant/galleries/${galleryId}/images/${imageId}`, {
    method: "DELETE"
  });
}

/**
 * Add an existing Media library item to a gallery — no new upload; the
 * gallery row references the library file's keys. Library caption/alt seed
 * the gallery image's fields.
 */
export async function addMediaToGallery(
  api: ApiClient,
  galleryId: string,
  item: MediaItem
): Promise<GalleryImage> {
  const image = await api.request<GalleryImage>(`/tenant/galleries/${galleryId}/images`, {
    method: "POST",
    body: JSON.stringify({
      key: item.key,
      thumbKey: item.thumbKey,
      width: item.width,
      height: item.height,
      caption: item.caption,
      alt: item.alt
    })
  });
  return { ...image, url: item.url ?? null, thumbUrl: item.thumbUrl ?? item.url ?? null };
}

/**
 * Full upload pipeline for one gallery photo: upload into the Media library
 * (browser-side resize into display + thumbnail variants), then register the
 * image row. Returns the new image with transient preview URLs attached.
 */
export async function uploadGalleryImage(
  api: ApiClient,
  galleryId: string,
  file: File
): Promise<GalleryImage> {
  const item = await uploadMedia(api, file);
  return addMediaToGallery(api, galleryId, item);
}

